import React, { useCallback, useEffect, useState } from "react";
import { Eye, X } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Input from "../Input/Input";
import APITable from "../Tables/APITable";
import Pagination from "../../components/Pagination/Pagination";
import Modal from "../Modal/Modal.jsx";
import ToggleSwitch from "../../components/ToggleSwitch/ToggleSwitch";
import Button from "../Button/Button";
import Notification from "../../components/Notification/Notification";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";
import { TableSkeleton } from "../../components/Skeleton/StatsSkeleton.jsx";

/* ===================== SERVICES ===================== */
import { apiGet, apiPost } from "../../apiService";

/* ===================== STYLES ===================== */
import styles from "./ManageApiSearch.module.scss";

const ManageApiSearch = () => {
  /* =========================
     STATE
  ========================= */
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ipModal, setIpModal] = useState(null);

  // Filters
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  // Modals
  const [viewModal, setViewModal] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [statusModal, setStatusModal] = useState(null);

  // Notification
  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  const showNotification = (msg, type = "success") => {
    setNotification(msg);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3000);
  };

  /* =========================
     HELPERS
  ========================= */
  const maskValue = (value, start = 3, end = 3) => {
    if (!value) return "--";
    if (value.length <= start + end) return value;
    const startPart = value.slice(0, start);
    const endPart = value.slice(-end);
    const maskedPart = "*".repeat(value.length - (start + end));
    return `${startPart}${maskedPart}${endPart}`;
  };

  const processModalIp = () => {
    const trimmed = ipModal?.newIp?.trim();
    if (!trimmed) return true;

    const ipRegex =
      /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;

    if (!ipRegex.test(trimmed)) {
      showNotification("Enter valid IP address", "error");
      return false;
    }

    if (!ipModal.ipList.includes(trimmed)) {
      setIpModal((prev) => ({
        ...prev,
        ipList: [...prev.ipList, trimmed],
        newIp: "",
      }));
    } else {
      setIpModal((prev) => ({ ...prev, newIp: "" }));
    }

    return true;
  };

  /* =========================
     API CALLS
  ========================= */
  const fetchApiKeys = useCallback(
    async (pageNo = currentPage, limit = rowsPerPage) => {
      try {
        setLoading(true);
        const res = await apiGet(
          "catalog",
          `/secrets/get-api-keys?page=${pageNo}&limit=${limit}&search=${search}&status=${statusFilter}`,
        );

        if (res?.status === "success") {
          const formatted = (res.data?.keys || []).map((k) => ({
            id: k.id,
            companyName: k.companyName || "--",
            name: k.keyName,
            serverType: k.serverType,
            client: maskValue(k.clientKey),
            clientRaw: k.clientKey,
            secret: k.clientSecret,
            ipCount: k.ipAddresses?.length || 0,
            ipList: k.ipAddresses?.map((ip) => ip.ipAddress) || [],
            status: k.status?.toUpperCase(),
            active: k.status === "Active",
          }));

          setUsers(formatted);
          setPagination(res.data.pagination);
        } else {
          showNotification("Failed to fetch API keys", "error");
        }
      } catch {
        showNotification("Something went wrong", "error");
      } finally {
        setLoading(false);
      }
    },
    [currentPage, rowsPerPage, search, statusFilter],
  );

  const fetchViewApiKey = async (id) => {
    try {
      setViewLoading(true);
      const res = await apiGet("catalog", `/secrets/view-key/${id}`);

      if (res?.status === "success") {
        const key = res.data.key;
        setViewModal({
          id: key.id,
          name: key.keyName,
          clientRaw: key.clientKey,
          clientSecret: key.clientSecret,
          ipList: key.ipAddresses?.map((ip) => ip.ipAddress) || [],
          companyName: key.companyName,
          serverType: key.serverType,
          status: key.status,
        });
      } else {
        showNotification("Failed to fetch key details", "error");
      }
    } catch (err) {
      showNotification("Something went wrong", "error");
    } finally {
      setViewLoading(false);
    }
  };

  const confirmToggle = async () => {
    if (!statusModal) return;

    const row = statusModal;
    const newStatus = row.active ? "Inactive" : "Active";
    const prevRow = { ...row };

    // Optimistic UI update
    setUsers((prev) =>
      prev.map((u) =>
        u.id === row.id
          ? { ...u, active: !u.active, status: newStatus.toUpperCase() }
          : u,
      ),
    );

    try {
      const res = await apiPost("catalog", "/secrets/status/update", {
        id: row.id,
        status: newStatus,
      });

      if (res?.status === "success") {
        showNotification("Status updated successfully");
      } else {
        throw new Error("API failed");
      }
    } catch (err) {
      setUsers((prev) => prev.map((u) => (u.id === row.id ? prevRow : u)));
      showNotification("Status update failed", "error");
    } finally {
      setStatusModal(null);
    }
  };

  /* =========================
     EFFECTS
  ========================= */
  useEffect(() => {
    fetchApiKeys(currentPage, rowsPerPage);
  }, [currentPage, rowsPerPage, statusFilter, fetchApiKeys]);

  /* =========================
     TABLE DATA
  ========================= */
  const tableData = users.map((item) => ({
    ...item,
    onView: () => fetchViewApiKey(item.id),
    onToggle: () => setStatusModal(item),
  }));

  const columns = [
    { label: "Key Name", key: "name" },
    { label: "Server Type", key: "serverType" },
    { label: "Client ID", key: "client" },
    { label: "IP Count", key: "ipAddress" },
    { label: "Status", key: "status" },
    { label: "Manage", key: "action" },
  ];

  /* =========================
     JSX
  ========================= */
  return (
    <>
      {notification && (
        <div className={styles["admin-manage-api-search__toast-container"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      <div className={styles["admin-manage-api-search__card"]}>
        {/* FILTERS */}
        <div className={styles["admin-manage-api-search__filter-bar"]}>
          <div className={styles["admin-manage-api-search__search-group"]}>
            <Input
              type="search"
              placeholder="Search Key Name"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setCurrentPage(1);
                  setSearch(searchInput);
                }
              }}
            />
            <Button
              text="Search"
              onClick={() => {
                setCurrentPage(1);
                setSearch(searchInput);
              }}
            />
          </div>

          <div className={styles["admin-manage-api-search__status-group"]}>
            <Input
              type="select"
              placeholder="All Status"
              value={statusFilter}
              onChange={(e) => {
                setCurrentPage(1);
                setStatusFilter(e.target.value);
              }}
              options={[
                { label: "Active", value: "Active" },
                { label: "Inactive", value: "Inactive" },
              ]}
            />
          </div>
        </div>

        {/* TABLE */}
        <div className={styles["admin-manage-api-search__table-wrapper"]}>
          {loading ? (
            <TableSkeleton columns={6} rows={3} />
          ) : (
            <APITable
              columns={columns}
              data={tableData}
              renderCell={(row, key) => {
                if (key === "serverType") {
                  return (
                    <span
                      className={`${styles["admin-manage-api-search__badge"]} ${
                        row.serverType === "Production"
                          ? styles["admin-manage-api-search__badge--production"]
                          : styles[
                              "admin-manage-api-search__badge--unproduction"
                            ]
                      }`}
                    >
                      {row.serverType}
                    </span>
                  );
                }

                if (key === "ipAddress") {
                  return (
                    <span
                      className={styles["admin-manage-api-search__ip-link"]}
                      onClick={() =>
                        setIpModal({
                          id: row.id,
                          ipList: row.ipList || [],
                          newIp: "",
                        })
                      }
                      title="Manage IPs"
                    >
                      {row.ipCount} IPs
                    </span>
                  );
                }

                if (key === "status") {
                  return (
                    <span
                      className={`${styles["admin-manage-api-search__badge"]} ${
                        row.active
                          ? styles["admin-manage-api-search__badge--active"]
                          : styles["admin-manage-api-search__badge--inactive"]
                      }`}
                    >
                      {row.status}
                    </span>
                  );
                }

                if (key === "action") {
                  return (
                    <div
                      className={styles["admin-manage-api-search__action-cell"]}
                    >
                      <div title="Toggle Status">
                        <ToggleSwitch
                          isOn={row.active}
                          onToggle={row.onToggle}
                        />
                      </div>

                      <div
                        className={styles["admin-manage-api-search__icon-btn"]}
                        onClick={row.onView}
                        title="View API Key"
                      >
                        <Eye size={18} strokeWidth={2.5} />
                      </div>
                    </div>
                  );
                }

                return row[key];
              }}
            />
          )}
        </div>

        {/* PAGINATION */}
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          rowsPerPage={rowsPerPage}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={(limit) => {
            setRowsPerPage(limit);
            setCurrentPage(1);
          }}
        />
      </div>

      <ConfirmModal
        open={!!statusModal}
        title="Are you sure you want to change the status?"
        onClose={() => setStatusModal(null)}
        onConfirm={confirmToggle}
      />

      {/* VIEW MODAL */}
      {viewModal && (
        <Modal onClose={() => setViewModal(null)} title="API Key Details">
          <div className={styles["admin-manage-api-search__modal-form"]}>
            {viewLoading ? (
              <p>Loading...</p>
            ) : (
              <>
                <Input label="Key Name" value={viewModal.name} disabled />
                <Input
                  label="Client ID"
                  value={viewModal.clientRaw}
                  showCopy
                  copyText={viewModal.clientRaw}
                  onCopySuccess={() =>
                    showNotification("Copied to clipboard", "success")
                  }
                />
                <Input
                  label="Client Secret"
                  value={viewModal.clientSecret}
                  showCopy
                  copyText={viewModal.clientSecret}
                  onCopySuccess={() =>
                    showNotification("Copied to clipboard", "success")
                  }
                />

                <div className={styles["admin-manage-api-search__modal-form"]}>
                  <label className="form-label" style={{ fontWeight: 600 }}>
                    Allowed IP Addresses
                  </label>
                  {viewModal.ipList?.length > 0 ? (
                    <div className={styles["admin-manage-api-search__ip-list"]}>
                      {viewModal.ipList.map((ip, index) => (
                        <span
                          className={styles["admin-manage-api-search__ip-chip"]}
                          key={index}
                        >
                          {ip}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p style={{ color: "#94a3b8", fontSize: "14px" }}>
                      No IP Restrictions applied.
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        </Modal>
      )}

      {/* MANAGE IP MODAL */}
      {ipModal && (
        <Modal onClose={() => setIpModal(null)} title="Manage IP Addresses">
          <div className={styles["admin-manage-api-search__modal-form"]}>
            {/* Existing IPs */}
            <div className={styles["admin-manage-api-search__ip-list"]}>
              {ipModal.ipList.map((ip, index) => (
                <div
                  key={index}
                  className={`${styles["admin-manage-api-search__ip-chip"]} ${styles["admin-manage-api-search__ip-chip--removable"]}`}
                >
                  {ip}
                  <div
                    className={styles["admin-manage-api-search__ip-remove"]}
                    onClick={() => {
                      setIpModal((prev) => ({
                        ...prev,
                        ipList: prev.ipList.filter((i) => i !== ip),
                      }));
                    }}
                  >
                    <X size={12} strokeWidth={3} />
                  </div>
                </div>
              ))}
            </div>

            {/* Add New IP */}
            <div className={styles["admin-manage-api-search__ip-input-row"]}>
              <Input
                placeholder="Enter new IP address (e.g. 192.168.1.1)"
                value={ipModal.newIp || ""}
                onChange={(e) =>
                  setIpModal((prev) => ({
                    ...prev,
                    newIp: e.target.value,
                  }))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    processModalIp();
                  }
                }}
              />
              <Button
                text="Add IP"
                onClick={() => {
                  const trimmed = ipModal.newIp?.trim();
                  if (!trimmed) return;
                  if (!ipModal.ipList.includes(trimmed)) {
                    setIpModal((prev) => ({
                      ...prev,
                      ipList: [...prev.ipList, trimmed],
                      newIp: "",
                    }));
                  }
                }}
              />
            </div>

            {/* Save Button */}
            <div className={styles["admin-manage-api-search__modal-actions"]}>
              <Button
                text="Save Changes"
                onClick={async () => {
                  const trimmed = ipModal.newIp?.trim();
                  let finalList = [...ipModal.ipList];

                  if (trimmed) {
                    const ipRegex =
                      /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;

                    if (!ipRegex.test(trimmed)) {
                      showNotification("Enter valid IP address", "error");
                      return;
                    }

                    if (!finalList.includes(trimmed)) {
                      finalList.push(trimmed);
                    }
                  }

                  try {
                    await apiPost(
                      "catalog",
                      `/secrets/update-ips/${ipModal.id}`,
                      {
                        ipAddress: finalList,
                      },
                    );
                    showNotification("IP Updated Successfully");
                    setIpModal(null);
                    fetchApiKeys();
                  } catch {
                    showNotification("Failed to update IPs", "error");
                  }
                }}
              />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default ManageApiSearch;
